<script setup lang="ts">
import EventBus from '@legacy/js/service/EventBus';
import axios from 'axios';
import { LogIn, Pencil } from 'lucide';
import { computed, onMounted, onUnmounted, ref, watch } from 'vue';
import { SphButton } from '@/components/base/button';
import { SphCheckbox } from '@/components/base/checkbox';
import { SphField } from '@/components/base/field';
import { SphInput } from '@/components/base/input';
import { SphSelect } from '@/components/base/select';
import { SphSpinner } from '@/components/base/spinner';
import EmailForm from '@/components/federation/EmailForm.vue';
import FederationNewsForm from '@/components/federation/FederationNewsForm.vue';
import { useI18n } from '@/composables/i18n';
import { useNotifications } from '@/composables/notifications';
import { useTableSort } from '@/composables/tableSort';
import TableSortHeader from './TableSortHeader.vue';

defineOptions({
    compatConfig: { MODE: 3 },
});

const props = defineProps<{
    federationId: number
    loginAllowed: boolean
    showSectionFilter?: boolean
    provinces?: Record<string, string>
    loginBaseUrl: string
    editBaseUrl: string
}>();

const { i18n } = useI18n();
const { notify } = useNotifications();

interface Section { id: number, name: string }
interface ClubSelection { id: number, selected: boolean }
interface Club {
    id: number
    name: string
    street: string
    zip: string
    city: string
    province: string | null
    sections: Section[]
}

const clubs = ref<Club[]>([]);
const sections = ref<Section[]>([]);
const loading = ref(true);
const filterSearch = ref('');
const filterSectionIds = ref<string[]>([]);
const filterProvince = ref<string | undefined>(undefined);
const filterCity = ref<string | undefined>(undefined);
const selectedOption = ref<undefined | string>(undefined);
const clubSelection = ref<ClubSelection[]>([]);
const selectedClubIds = ref<number[]>([]);

const localStorageKey = `federation-${props.federationId}-club-selection`;

const sectionOptions = computed(() =>
    sections.value.map((s) => ({ value: String(s.id), label: s.name })),
);

const provinceOptions = computed(() => {
    const seen = new Set<string>();
    return clubs.value
        .filter((c) => c.province && props.provinces?.[c.province])
        .filter((c) => {
            if (seen.has(c.province!)) {
                return false;
            }
            seen.add(c.province!);
            return true;
        })
        .map((c) => ({ value: c.province!, label: props.provinces![c.province!] }))
        .sort((a, b) => a.label.localeCompare(b.label));
});

const cityOptions = computed(() => {
    const source = filterProvince.value
        ? clubs.value.filter((c) => c.province === filterProvince.value)
        : clubs.value;
    const seen = new Set<string>();
    return source
        .filter((c) => c.city)
        .filter((c) => {
            if (seen.has(c.city)) {
                return false;
            }
            seen.add(c.city);
            return true;
        })
        .map((c) => ({ value: c.city, label: c.city }))
        .sort((a, b) => a.label.localeCompare(b.label));
});

const filtered = computed(() =>
    clubs.value.filter((c) => {
        if (filterSearch.value && !c.name.toLowerCase().includes(filterSearch.value.toLowerCase())) {
            return false;
        }
        if (filterSectionIds.value.length > 0 && !filterSectionIds.value.some((id) => c.sections.map((s) => String(s.id)).includes(id))) {
            return false;
        }
        if (filterProvince.value && c.province !== filterProvince.value) {
            return false;
        }
        if (filterCity.value && c.city !== filterCity.value) {
            return false;
        }
        return true;
    }),
);

const selectedClubs = computed(() => {
    return filtered.value.filter((c) => clubSelection.value.find((s) => s.id === c.id)?.selected);
});

const selectOptions = [
    { value: 'email', label: i18n.t('E-Mail senden') },
    { value: 'news', label: i18n.t('Neuigkeit senden') },
];

const { sortKey, sortDirection, toggleSort, sorted } = useTableSort(filtered, {
    name: (c) => c.name,
    address: (c) => c.city,
    province: (c) => (props.provinces ?? {})[c.province ?? ''] ?? '',
}, { storageKey: 'federation:clubList:sort' });

watch(filterProvince, () => {
    filterCity.value = undefined;
});

function loadSections() {
    axios.get(`/federations/${props.federationId}/sections`)
        .then((r) => {
            sections.value = r.data.data.map((d: any) => ({ id: d.id, name: d.name }));
        })
        .catch(() => notify.error('error.loadingData'));
}

let unsubscribeSection: (() => void) | undefined;

type Action = 'email' | 'news';
const action = ref<Action | undefined>(undefined);
const toggleAll = ref<boolean>(false);

function selectionAction() {
    if (selectedOption.value === 'email') {
        selectedClubIds.value = selectedClubs.value.map((c) => c.id);
        action.value = 'email';
    } else if (selectedOption.value === 'news') {
        action.value = 'news';
    }
}

function saveSelectionToStorage() {
    const selectedIds = clubSelection.value.filter((c) => c.selected).map((c) => c.id);
    localStorage.setItem(localStorageKey, JSON.stringify(selectedIds));
}

function loadSelectionFromStorage() {
    const stored = localStorage.getItem(localStorageKey);
    if (stored) {
        const selectedIds: number[] = (() => {
            try {
                return JSON.parse(stored) as number[];
            } catch {
                return [];
            }
        })();
        clubSelection.value.forEach((c) => {
            c.selected = selectedIds.includes(c.id);
        });
    }
}

function clearSelectionStorage() {
    localStorage.removeItem(localStorageKey);
}

watch(action, (newAction) => {
    if (newAction) {
        window.location.hash = newAction;
    } else {
        history.replaceState(null, '', window.location.pathname + window.location.search);
        clearSelectionStorage();
    }
});

watch(clubSelection, saveSelectionToStorage, { deep: true });

watch(selectedClubIds, (newIds) => {
    if (action.value !== 'email') {
        return;
    }
    clubSelection.value.forEach((c) => {
        c.selected = newIds.includes(c.id);
    });
});

watch(toggleAll, (newValue) => {
    clubSelection.value.forEach((c) => {
        if (filtered.value.some((f) => f.id === c.id)) {
            c.selected = newValue;
        }
    });
});

onMounted(() => {
    const hash = window.location.hash.slice(1);
    if (hash === 'email' || hash === 'news') {
        action.value = hash;
        selectedOption.value = hash;
    } else if (hash) {
        history.replaceState(null, '', window.location.pathname + window.location.search);
    }

    loading.value = true;
    axios.get(`/federation/federations/${props.federationId}/clubs`)
        .then((r) => {
            clubs.value = r.data.data.map((d: any) => ({
                id: d.id,
                name: d.name,
                street: d.street ?? '',
                zip: d.zip ?? '',
                city: d.city ?? '',
                province: d.province ?? null,
                sections: (d.sections ?? []).map((s: any) => ({ id: s.id, name: s.name })),
            }));
            clubSelection.value = clubs.value.map((c) => ({ id: c.id, selected: false }));
            if (action.value) {
                loadSelectionFromStorage();
                if (action.value === 'email') {
                    selectedClubIds.value = selectedClubs.value.map((c) => c.id);
                }
            }
        })
        .catch(() => notify.error('error.loadingData'))
        .finally(() => {
            loading.value = false;
        });
    if (props.showSectionFilter !== false) {
        loadSections();
    }
    unsubscribeSection = EventBus.$on('section:changed', (federationId: number) => {
        if (federationId === props.federationId) {
            loadSections();
        }
    });
});

onUnmounted(() => {
    unsubscribeSection?.();
});

defineExpose({ clubs, sections, filtered, sorted, sortKey, sortDirection, toggleSort, provinceOptions, cityOptions, filterSearch, filterSectionIds, filterProvince, filterCity });

const printPage = () => window.print();

const noAction = computed(() => action.value === undefined);
function emailsSent() {
    notify.success('E-Mails wurden verschickt.');
    action.value = undefined;
    selectedOption.value = undefined;
}
function newsSent() {
    notify.success('Neuigkeit wurde gesendet.');
    action.value = undefined;
    selectedOption.value = undefined;
}
</script>

<template>
    <div>
        <SphSpinner
            v-if="loading"
            variant="centered"
        />
        <template v-else>
            <div
                v-if="noAction"
                class="kgr-row kgr-postfix kgr-postfix--between items-end"
            >
                <div class="flex gap-4">
                    <SphField
                        v-if="showSectionFilter !== false && noAction"
                        :label="i18n.t('Bereich')"
                    >
                        <SphSelect
                            v-model="filterSectionIds"
                            :options="sectionOptions"
                            :placeholder="i18n.t('Alle Bereiche')"
                            clearable
                            multiple
                        />
                    </SphField>
                    <SphField
                        v-if="noAction"
                        :label="i18n.t('Bundesland')"
                    >
                        <SphSelect
                            v-model="filterProvince"
                            :options="provinceOptions"
                            :placeholder="i18n.t('Alle Bundesländer')"
                            clearable
                        />
                    </SphField>
                    <SphField
                        v-if="noAction"
                        :label="i18n.t('Ort')"
                    >
                        <SphSelect
                            v-model="filterCity"
                            :options="cityOptions"
                            :placeholder="i18n.t('Alle Orte')"
                            clearable
                        />
                    </SphField>
                </div>
                <SphInput
                    v-if="noAction"
                    v-model="filterSearch"
                    :placeholder="i18n.t('Suche in dieser Liste ...')"
                />
            </div>

            <div v-if="action === 'email'">
                <EmailForm
                    v-model:selected-ids="selectedClubIds"
                    :federation-id="props.federationId"
                    :all-clubs="clubs"
                    :sections="sections"
                    :selected-section-ids="filterSectionIds.map(id => parseInt(id))"
                    @cancel="action = undefined;selectedOption = undefined"
                    @done="emailsSent"
                />
            </div>

            <div v-else-if="action === 'news'">
                <FederationNewsForm
                    :federation-id="props.federationId"
                    target="clubs"
                    :institutions="clubs"
                    :preselected-ids="selectedClubs.map(c => c.id)"
                    @cancel="action = undefined;selectedOption = undefined"
                    @saved="newsSent"
                />
            </div>

            <div
                v-else-if="noAction && filtered.length === 0"
                class="kgr-alert kgr-alert--info"
            >
                {{ i18n.t('Keine Vereine eingetragen') }}
            </div>

            <div v-else-if="noAction">
                <div class="kgr-row">
                    <table class="kgr-table kgr-table--striped kgr-table--block table-groups">
                        <thead>
                            <tr class="kgr-table__row">
                                <th class="kgr-table__cell kgr-table__cell--headline">
                                    <SphCheckbox
                                        v-model="toggleAll"
                                    />
                                </th>
                                <TableSortHeader
                                    :label="i18n.t('Name des Vereins')"
                                    sort-key="name"
                                    :active-key="sortKey"
                                    :direction="sortDirection"
                                    @sort="toggleSort"
                                />
                                <TableSortHeader
                                    :label="i18n.t('Adresse')"
                                    sort-key="address"
                                    :active-key="sortKey"
                                    :direction="sortDirection"
                                    @sort="toggleSort"
                                />
                                <TableSortHeader
                                    :label="i18n.t('Bundesland')"
                                    sort-key="province"
                                    :active-key="sortKey"
                                    :direction="sortDirection"
                                    @sort="toggleSort"
                                />
                                <th class="kgr-table__cell kgr-table__cell--headline kgr-table__cell--actions">
                                    {{ i18n.t('options') }}
                                </th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr
                                v-for="club in sorted"
                                :key="club.id"
                                class="kgr-table__row"
                            >
                                <td class="kgr-table__cell">
                                    <SphCheckbox
                                        v-model="clubSelection.find(c => c.id === club.id)!.selected"
                                    />
                                </td>
                                <td class="kgr-table__cell">
                                    <a
                                        v-if="loginAllowed"
                                        :href="`${loginBaseUrl}/${club.id}/dashboard`"
                                        class="kgr-link kgr-link--unobtrusive"
                                    >{{ club.name }}</a>
                                    <template v-else>
                                        {{ club.name }}
                                    </template>
                                </td>
                                <td class="kgr-table__cell">
                                    <div class="kgr-text kgr-text--truncated kgr-text--noWrap">
                                        {{ club.street }}
                                    </div>
                                    <div class="kgr-text kgr-text--truncated kgr-text--noWrap">
                                        {{ club.zip }} {{ club.city }}
                                    </div>
                                </td>
                                <td class="kgr-table__cell">
                                    {{ (provinces ?? {})[club.province ?? ''] || '-' }}
                                </td>
                                <td class="kgr-table__cell kgr-table__cell--actions">
                                    <template v-if="loginAllowed">
                                        <SphButton
                                            :icon="Pencil"
                                            as="a"
                                            :href="`${editBaseUrl}/${club.id}/edit`"
                                            :title="i18n.t('edit')"
                                            variant="ghost"
                                            size="md"
                                            class="kgr-table__action"
                                        />
                                        <SphButton
                                            :icon="LogIn"
                                            as="a"
                                            :href="`${loginBaseUrl}/${club.id}/dashboard`"
                                            :title="i18n.t('login')"
                                            variant="ghost"
                                            size="md"
                                            class="kgr-table__action"
                                        />
                                    </template>
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </div>
                <div class="kgr-postfix kgr-postfix--between">
                    <div class="kgr-postfix__fixed">
                        <SphSelect
                            v-model="selectedOption"
                            :options="selectOptions"
                            :disabled="selectedClubs.length === 0"
                            @update:model-value="selectionAction"
                        />
                    </div>
                    <div class="kgr-postfix__fixed">
                        <SphButton
                            :label="i18n.t('print')"
                            variant="primary"
                            class="kgr-hidden-print"
                            @click="printPage"
                        />
                    </div>
                </div>
            </div>
        </template>
    </div>
</template>
